// SINGLE SOURCE OF TRUTH for every company fact the site states.
//
// Rules (Phase 0 of the recovery plan, report section 8):
//   - Every component, JSON-LD block and the llms.txt generator reads from
//     here. Never hard-code one of these values anywhere else.
//   - `null` means "not confirmed by the owner". Callers MUST omit the field
//     or sentence when a value is null. Never print a placeholder, "[CONFIRM]",
//     or an invented number.
//   - To publish a fact: replace the `null` with the owner-approved value.
//     Nothing else needs to change.
//
// Values that are not null below were already published on the live site
// before Phase 0 (address, contact details, profile URLs, leadership,
// certifications) and are carried over unchanged. They are not new claims.

export type PriceTier = {
  /** e.g. "GHL Specialist" */
  role: string
  /** USD per month, part-time engagement, or null if not approved */
  partTimeMonthly: number | null
  /** USD per month, full-time engagement, or null if not approved */
  fullTimeMonthly: number | null
  /** USD per hour, or null if not approved */
  hourly: number | null
}

export type ProjectPrice = {
  name: string
  /** USD "starts from" price, or null if not approved */
  startsFrom: number | null
  description: string
}

export type Leader = {
  id: string
  name: string
  jobTitle: string
  linkedin: string
  image: string
}

export const FACTS = {
  brand: {
    name: 'GHL Prime',
    legalName: 'GHL Prime LLC',
    url: 'https://ghlprime.com',
    logo: 'https://ghlprime.com/logo.png',
    email: 'info@ghlprime.com',
    phone: '+1-505-207-5189',
    description:
      'GoHighLevel expert team and fulfilment partner for agencies and business owners: GHL setup, automation, A2P 10DLC, SaaS mode, white-label support, AI voice agents and custom development.',
  },

  address: {
    streetAddress: '4801 Lang Ave NE, Suite 110',
    addressLocality: 'Albuquerque',
    addressRegion: 'NM',
    postalCode: '87109',
    addressCountry: 'US',
  },

  areaServed: ['US', 'CA', 'GB', 'AU'],

  // ---- UNCONFIRMED: owner must fill (report section 8 / section 16) --------
  /** ISO date or year, e.g. "2023". null = omit everywhere. */
  foundingDate: null as string | null,
  /** Parent company. Only set once ownership is confirmed. null = omit. */
  parentOrganization: null as { name: string; url: string } | null,
  /** Total people. null = omit. */
  teamSize: null as number | null,
  /** Certified GHL experts. null = omit. */
  certifiedExperts: null as number | null,
  /** GHL accounts delivered, e.g. 400. null = omit. */
  accountsDelivered: null as number | null,
  /** Schema.org priceRange. null = omit. */
  priceRange: null as string | null,
  /** Published response-time promise, e.g. "within 4 business hours". null = omit. */
  responseTime: null as string | null,
  /** Plain statement of where the delivery team sits. null = omit. */
  deliveryLocation: null as string | null,

  // ---- Support coverage (already stated across the live site) ------------
  supportCoverage: '24/7 white-label client support',

  // ---- Leadership (already published on the live site) -------------------
  leaders: [
    {
      id: 'jewel-rana',
      name: 'Jewel Rana',
      jobTitle: 'CEO & Co-Founder',
      linkedin: 'https://www.linkedin.com/in/thejewelrana/',
      image: 'https://ghlprime.com/CEO.png',
    },
    {
      id: 'niyamul-islam-sajal',
      name: 'Niyamul Islam Sajal',
      jobTitle: 'COO & Co-Founder',
      linkedin: 'https://www.linkedin.com/in/niyamulislam/',
      image: 'https://ghlprime.com/COO.png',
    },
  ] as Leader[],

  // ---- Profiles (only URLs already published on the live site) -----------
  // Add Clutch / G2 / Crunchbase / YouTube here ONLY once the profile exists.
  sameAs: [
    'https://www.linkedin.com/company/ghl-prime-llc',
    'https://www.facebook.com/profile.php?id=61573474861100',
    'https://www.upwork.com/agencies/ghlprime/',
  ] as string[],

  // ---- Certifications (names as published on the live site) --------------
  certifications: [
    'GoHighLevel Certified Admin',
    'A2P 10DLC Compliance',
    'HIPAA Compliance',
    'AI Voice',
    'Funnel Builder',
    'Paid Ads',
    'WhatsApp Integration',
  ] as string[],

  whoWeServe:
    'marketing agencies reselling GoHighLevel, SaaS founders launching on SaaS Mode, local service businesses, and coaches or consultants who need one GoHighLevel specialist',

  services: [
    { name: 'GHL Setup & Configuration', url: '/services/ghl-setup', summary: 'Sub-accounts, pipelines, calendars, A2P/Twilio and full GoHighLevel account setup.' },
    { name: 'Workflow Automation', url: '/services/automation', summary: 'Speed-to-lead, appointment reminders, pipeline automation, review requests and database reactivation.' },
    { name: 'SaaS CRM Launch', url: '/services/saas-crm', summary: 'Stripe rebilling, plan tiers and white-label SaaS Mode launch for agencies.' },
    { name: 'White-Label Support', url: '/services/white-label-support', summary: '24/7 expert client support delivered under the agency brand.' },
    { name: 'AI Agent Builder', url: '/services/ai-agent-builder', summary: 'Lead-qualifying chatbots, voice receptionists and AI call center agents.' },
    { name: 'Vibe Coding & Custom Dev', url: '/services/vibe-coding', summary: 'AI-assisted custom development and integrations beyond native GoHighLevel features.' },
    { name: 'Custom SaaS Development', url: '/services/custom-saas-development', summary: 'Client portals and dashboards built with Claude Code and Supabase.' },
    { name: 'Figma to Code', url: '/services/figma-to-code', summary: 'Pixel-perfect design-to-production-code builds.' },
    { name: 'Web & Mobile App Development', url: '/services/app-development', summary: 'iOS, Android and web apps from MVP to production.' },
    { name: 'SaaS Customer Support', url: '/services/saas-customer-support', summary: 'White-labeled ticket handling and client onboarding with 24/7 coverage.' },
    { name: 'GHL Experts for Agencies', url: '/services/ghl-experts-for-agencies', summary: 'Agency fulfilment partnership with a named certified specialist and white-labeled delivery.' },
  ] as { name: string; url: string; summary: string }[],

  // Homepage stat band. These four figures were already shown on the live
  // site (they were the targets of the old count-up animation), so they are
  // carried over unchanged. They have NOT been re-confirmed by the owner, and
  // "years in production" is in tension with the founding-date question in
  // the report. Set any of them to null to remove that tile entirely.
  homepageStats: [
    { label: 'Live products', value: 90 as number | null, suffix: '+' },
    { label: 'Users served', value: 550 as number | null, suffix: '+' },
    { label: 'Enterprise systems', value: 15 as number | null, suffix: '' },
    { label: 'Years in production', value: 7 as number | null, suffix: '+' },
  ],

  // ---- Pricing ------------------------------------------------------------
  // Flip `approved` to true ONLY after the owner signs off on the numbers.
  // While false: /pricing renders noindex, is excluded from the sitemap and
  // llms.txt, and the /services/cost-effective-ghl-team redirect stays off.
  pricing: {
    approved: false,
    currency: 'USD',
    tiers: [
      { role: 'GHL Specialist', partTimeMonthly: null, fullTimeMonthly: null, hourly: null },
      { role: 'GHL Expert', partTimeMonthly: null, fullTimeMonthly: null, hourly: null },
      { role: 'GHL Solution Architect', partTimeMonthly: null, fullTimeMonthly: null, hourly: null },
    ] as PriceTier[],
    projects: [
      { name: 'GoHighLevel account setup', startsFrom: null, description: 'Sub-accounts, pipelines, calendars, forms and integrations configured for your use case.' },
      { name: 'Workflow automation build', startsFrom: null, description: 'Lead capture, follow-up, reminders and pipeline automation.' },
      { name: 'White-label SaaS Mode launch', startsFrom: null, description: 'Branded SaaS Mode setup with Stripe rebilling and onboarding flows.' },
      { name: 'AI voice agent build', startsFrom: null, description: 'Voice receptionist or lead-qualification agent connected to GoHighLevel.' },
    ] as ProjectPrice[],
    included: [
      'Certified GoHighLevel specialists',
      'White-label delivery under your brand',
      'Daily project updates in Slack, email or a shared board',
      'System walkthrough and handoff session',
    ],
  },
} as const

export type Facts = typeof FACTS

/** True when real, owner-approved pricing is available to publish. */
export function pricingIsPublishable(): boolean {
  const p = FACTS.pricing
  if (!p.approved) return false
  return p.tiers.some(
    (t) => t.partTimeMonthly !== null || t.fullTimeMonthly !== null || t.hourly !== null,
  )
}

/** Stats that have a value; tiles with a null value are never rendered. */
export function visibleHomepageStats() {
  return FACTS.homepageStats.filter((s) => s.value !== null) as {
    label: string
    value: number
    suffix: string
  }[]
}

const usd = (n: number) => `$${n.toLocaleString('en-US')}`

/** Lowest published monthly price across tiers, or null. */
export function lowestMonthlyPrice(): number | null {
  if (!pricingIsPublishable()) return null
  const vals: number[] = []
  for (const t of FACTS.pricing.tiers) {
    if (t.partTimeMonthly !== null) vals.push(t.partTimeMonthly)
    if (t.fullTimeMonthly !== null) vals.push(t.fullTimeMonthly)
  }
  return vals.length ? Math.min(...vals) : null
}

/**
 * "At a glance" lines, in display order. Lines whose value is null are
 * omitted entirely. Shared by the homepage/about block (T-9.1) and the
 * llms.txt generator (T-9.2) so the two can never disagree.
 */
export function glanceItems(): { term: string; detail: string }[] {
  const f = FACTS
  const items: { term: string; detail: string }[] = []
  items.push({
    term: 'Company overview',
    detail: 'A GoHighLevel implementation and fulfillment team for agencies and business owners, with specialist support across CRM setup, automation, AI, and SaaS Mode.',
  })
  items.push({ term: 'Clients we serve', detail: 'Marketing agencies reselling GoHighLevel; SaaS founders launching with SaaS Mode; local service businesses; and coaches or consultants who need a dedicated GHL specialist.' })
  items.push({ term: 'GoHighLevel services', detail: f.services.slice(0, 6).map((s) => s.name).join(', ') })
  if (f.teamSize !== null) items.push({ term: 'Team size', detail: `${f.teamSize} people` })
  if (f.certifiedExperts !== null) items.push({ term: 'Certified GHL experts', detail: `${f.certifiedExperts}+` })
  if (f.accountsDelivered !== null) items.push({ term: 'Accounts delivered', detail: `${f.accountsDelivered.toLocaleString('en-US')}+` })
  if (f.certifications.length) items.push({ term: 'GoHighLevel certifications', detail: f.certifications.join(', ') })
  if (f.leaders.length) {
    items.push({
      term: 'Leadership',
      detail: f.leaders.map((leader) => `${leader.name}, ${leader.jobTitle}`).join('; '),
    })
  }
  const marketNames: Record<string, string> = { US: 'United States', CA: 'Canada', GB: 'United Kingdom', AU: 'Australia' }
  if (f.areaServed.length) {
    items.push({ term: 'Markets served', detail: f.areaServed.map((market) => marketNames[market] || market).join(', ') })
  }
  const low = lowestMonthlyPrice()
  if (low !== null) items.push({ term: 'Pricing', detail: `Dedicated experts start from ${usd(low)}/month` })
  if (f.responseTime) items.push({ term: 'Response time', detail: f.responseTime })
  items.push({ term: 'Client support', detail: '24/7 white-label client support for agency clients, delivered under the agency’s brand.' })
  const loc = `${f.address.streetAddress}, ${f.address.addressLocality}, ${f.address.addressRegion} ${f.address.postalCode}, USA`
  items.push({ term: 'Headquarters', detail: f.deliveryLocation ? `${loc}. ${f.deliveryLocation}` : loc })
  items.push({ term: 'White-label delivery', detail: 'Backend GoHighLevel implementation and client support delivered under your agency’s brand.' })
  items.push({ term: 'Project workflow', detail: 'Scope and audit → build and configure → walkthrough and handoff → ongoing support.' })
  return items
}

/**
 * Confirmed headline figures as sentences (team size, certified experts,
 * accounts delivered, starting price). Empty string while all are null.
 */
export function glanceFigures(): string {
  const f = FACTS
  const parts: string[] = []
  const stats: string[] = []
  if (f.teamSize !== null) stats.push(`${f.teamSize} people`)
  if (f.certifiedExperts !== null) stats.push(`${f.certifiedExperts}+ certified GHL experts`)
  if (f.accountsDelivered !== null) stats.push(`${f.accountsDelivered.toLocaleString('en-US')}+ accounts delivered`)
  if (stats.length) parts.push(`${stats.join(', ')}.`)
  const low = lowestMonthlyPrice()
  if (low !== null) parts.push(`Dedicated experts start from ${usd(low)}/month.`)
  return parts.join(' ')
}

/** One-paragraph summary built only from non-null facts. */
export function glanceSummary(): string {
  const intro = `${FACTS.brand.name} is a GoHighLevel expert team and fulfillment partner for marketing agencies and business owners.`
  const figures = glanceFigures()
  return figures ? `${intro} ${figures}` : intro
}
